// Tab-scoped sign-in records (PB-5461). Three small facts, each about ONE
// tab's round trip through Keycloak, so they live in `sessionStorage`: another
// tab has its own sign-in and must never read this one's.
//
//   pb.auth.returnTo       {path, at}  the page this tab's sign-in started from
//   pb.auth.autoRestartAt  <ms>        the last automatic restart of a sign-in
//   pb.auth.passwordChange {at}        a password change started from this tab
//
// None of them is a credential. Every access is wrapped: private mode, blocked
// site data or a full quota make the accessor THROW, and sign-in has to keep
// working without them. Each reader says what an unreadable record means.
//
// The one-tab sign-in lock (d21) is shared between tabs, so its records live
// apart, in `sign-in-lease.ts`.

import { isSafeAuthReturnTo } from "@playbookai/auth-client/return-to";

const RETURN_TO_KEY = "pb.auth.returnTo";
const AUTO_RESTART_KEY = "pb.auth.autoRestartAt";
const PASSWORD_CHANGE_KEY = "pb.auth.passwordChange";

/** At most one automatic restart per tab in this window, so a failing sign-in
 *  can never loop through Keycloak. */
export const AUTO_RESTART_WINDOW_MS = 2 * 60_000;

/** A password change older than this was abandoned: Keycloak's own page is gone
 *  by then, so the marker must stop holding back a sign-in restart. */
export const PASSWORD_CHANGE_MARKER_TTL_MS = 30 * 60_000;

function read(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

/** True when the value was stored. */
function write(key: string, value: string): boolean {
  try {
    window.sessionStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

function remove(key: string): void {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // Nothing stored can be read back either, so there is nothing to clear.
  }
}

function readTimestamp(raw: string | null, field?: "at"): number | null {
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    const value =
      field === undefined
        ? parsed
        : typeof parsed === "object" && parsed !== null
          ? (parsed as Record<string, unknown>)[field]
          : undefined;
    return typeof value === "number" && Number.isFinite(value) ? value : null;
  } catch {
    return null;
  }
}

/** Keep the page this tab's sign-in starts from. An unsafe path is not kept. */
export function keepReturnTo(path: string, now: number = Date.now()): void {
  if (!isSafeAuthReturnTo(path)) return;
  write(RETURN_TO_KEY, JSON.stringify({ path, at: now }));
}

/**
 * Read AND remove the kept page. `null` when there is none, or when what is
 * there is malformed or unsafe: the record is browser state another script on
 * this origin could have written, so it goes through the same allowlist as the
 * server's `returnTo` before anything navigates to it.
 */
export function takeReturnTo(): string | null {
  const raw = read(RETURN_TO_KEY);
  remove(RETURN_TO_KEY);
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    const path =
      typeof parsed === "object" && parsed !== null
        ? (parsed as { path?: unknown }).path
        : undefined;
    return isSafeAuthReturnTo(path) ? path : null;
  } catch {
    return null;
  }
}

/** Forget the kept page, as sign-out does. */
export function forgetReturnTo(): void {
  remove(RETURN_TO_KEY);
}

/**
 * Claim this tab's one automatic sign-in restart. True when the last one is
 * older than {@link AUTO_RESTART_WINDOW_MS} (or there was none) AND this one
 * could be recorded. A tab that cannot record the restart cannot promise the
 * next failure will not restart again, so it gets no automatic restart at all:
 * a Try again button is a far better failure than a redirect loop.
 */
export function claimAutoRestart(now: number = Date.now()): boolean {
  const last = readTimestamp(read(AUTO_RESTART_KEY));
  if (last !== null && now >= last && now - last < AUTO_RESTART_WINDOW_MS) {
    return false;
  }
  return write(AUTO_RESTART_KEY, String(now));
}

/** Note that this tab started a password change. Called only once the start
 *  call has returned Keycloak's address (PB-5461, AC-21). */
export function markPasswordChangeStarted(now: number = Date.now()): void {
  write(PASSWORD_CHANGE_KEY, JSON.stringify({ at: now }));
}

/**
 * Read AND remove the password-change marker. True only for a marker younger
 * than {@link PASSWORD_CHANGE_MARKER_TTL_MS}: an abandoned change must never
 * hold back a later sign-in's automatic restart.
 */
export function takePasswordChangeMarker(now: number = Date.now()): boolean {
  const at = readTimestamp(read(PASSWORD_CHANGE_KEY), "at");
  remove(PASSWORD_CHANGE_KEY);
  return at !== null && now >= at && now - at < PASSWORD_CHANGE_MARKER_TTL_MS;
}

/** Forget the password-change marker, as every sign-in start does. */
export function forgetPasswordChangeMarker(): void {
  remove(PASSWORD_CHANGE_KEY);
}
